/* eslint no-param-reassign: "error" */
import { createSlice } from '@reduxjs/toolkit';
import { resetErrors } from '../utilities/helperUtility';

const initialState = {
  totalPrice: 0,
  date: null,
  available: 0,
  quantity: 1,
  totalQuantity: 0,
  variants: {},
  timeSlot: null,
  timeSlots: [],
  selectedTimeslot: null,
  variantSelected: null,
  variantsSelected: [],
  selectedLocation: null,
  selectedResource: null,
  termSelected: {},
  bookingFields: [],
  bookings: [],
  additionalDetails: {},
  additionalDetailsValidationErrors: [],
  multipleSlotsCount: 1,
  recurringMultipleSlots: false,
  customerDetails: {},
  customerDetailsValidationErrors: [],
  resourceValidationErrors: null,
  locationValidationErrors: null,
  newVariantOption: null,
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    changeAdditionalDetails(state, action) {
      const { payload } = action;
      const { additionalDetailsValidationErrors: errors } = state;
      if (payload.field.repeated && payload.index != null) {
        const values = state.additionalDetails[payload.field.key];
        values[payload.index] = payload.value;
        state.additionalDetails[payload.field.key] = values;
        state.additionalDetailsValidationErrors = resetErrors(
          errors,
          `${payload.field.key}-${payload.index}`,
        );
      } else {
        state.additionalDetails[payload.field.key] = payload.value;
        state.additionalDetailsValidationErrors = resetErrors(errors, payload.field.key);
      }
    },
    changeCustomerDetails(state, action) {
      const { payload } = action;
      const { customerDetailsValidationErrors: errors } = state;
      state.customerDetails[payload.field] = payload.value;
      state.customerDetailsValidationErrors = resetErrors(errors, payload.field);
    },
    onAddRepeatedFieldItems(state, action) {
      const { payload } = action;
      const details = { ...state.additionalDetails };
      const fieldsSettings = payload.fieldsSettings || [];
      fieldsSettings.forEach((field) => {
        if (field.repeated) {
          if (!details[field.key]) details[field.key] = [];
          details[field.key].push('');
        }
      });
      state.additionalDetails = details;
    },
    onRemoveRepeatedFieldItems(state, action) {
      const { payload } = action;
      const details = { ...state.additionalDetails };
      const fieldsSettings = payload.fieldsSettings || [];
      fieldsSettings.forEach((field) => {
        if (field.repeated) {
          if (!details[field.key]) details[field.key] = [];
          details[field.key].splice(payload.quantity);
        }
      });
      state.additionalDetails = details;
    },
    onSetCustomerErrors(state, action) {
      state.customerDetailsValidationErrors = action.payload;
    },
    onSetDetailsErrors(state, action) {
      state.additionalDetailsValidationErrors = action.payload;
    },
    onSetLocationErrors(state, action) {
      state.locationValidationErrors = action.payload;
    },
    onSetResourceErrors(state, action) {
      state.resourceValidationErrors = action.payload;
    },
    setMultipleSlotsCount(state, action) {
      state.multipleSlotsCount = action.payload;
      const currentTimeslot = state.timeSlot?.timeSlot;
      if (action.payload === 2 && state.timeSlots.length === 0 && currentTimeslot) {
        state.timeSlots = [currentTimeslot];
      }
    },
    setRecurringMultipleSlots(state, action) {
      state.recurringMultipleSlots = action.payload;
    },
    setBookingFields(state, action) {
      state.bookingFields = action.payload;
    },
    setCustomerDetails(state, action) {
      state.customerDetails = action.payload;
      state.customerDetailsValidationErrors = [];
    },
    setDateRange(state, action) {
      state.dateRange = action.payload;
    },
    setNewVariantOption(state, action) {
      state.newVariantOption = action.payload;
    },
    setOrderDetails(state, action) {
      const { payload } = action;
      Object.keys(payload).forEach((key) => {
        state[key] = payload[key];
      });
    },
    setSelectedLocation(state, action) {
      state.selectedLocation = action.payload;
    },
    setSelectedResource(state, action) {
      state.selectedResource = action.payload;
    },
    setSelectedTerm(state, action) {
      state.termSelected = action.payload;
    },
    setSelectedTimeslot(state, action) {
      state.selectedTimeslot = action.payload;
    },
    setSelectedVariant(state, action) {
      state.variantSelected = action.payload;
    },
    setSelectedVariants(state) {
      const variantsSelected = [];
      Object.keys(state.variants).forEach((key) => {
        const variant = state.variants[key];
        if (variant && variant.quantity > 0) {
          variantsSelected.push(variant.external_id);
        }
      });
      state.variantsSelected = variantsSelected;
    },
    setTimeSlot(state, action) {
      state.timeSlot = action.payload;
      const count = state.multipleSlotsCount;
      const timeSlot = action.payload?.timeSlot;

      if (count > 1 && timeSlot) {
        const timeslot = timeSlot;
        const timeSlotsLength = state.timeSlots.length;
        let newTimeSlots = [...state.timeSlots];

        if (count > timeSlotsLength) {
          newTimeSlots.push(timeslot);
        } else if (count === timeSlotsLength) {
          newTimeSlots.splice(-1, 1, timeslot)
        }

        state.timeSlots = newTimeSlots;
      }
    },
    removeTimeSlots(state) {
      state.timeSlot = null;
      state.multipleSlotsCount = 1;
      state.timeSlots = [];
    },
    removeTimeSlotByIndex(state, action) {
      let newTimeSlots = [...state.timeSlots];
      if (newTimeSlots.length > 1) {
        newTimeSlots.splice(action.payload, 1);
        state.timeSlots = newTimeSlots;
        state.multipleSlotsCount = newTimeSlots.length + 1;
      } else {
        state.timeSlot = null;
        state.timeSlots = [];
        state.multipleSlotsCount = 1;
      }
    }
  },
});

export const ordersActions = ordersSlice.actions;

export default ordersSlice.reducer;
